import { Rocket, Crown, Sparkles, Check } from "lucide-react";
import { motion, useReducedMotion, type TargetAndTransition, type VariantLabels } from "framer-motion";
import { Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { GlowCard } from "@/components/ui/spotlight-card";
import { ShineBorder } from "@/components/ui/shine-border";
import { beginCheckout } from "@/lib/analytics";
import { LAUNCH_BENEFITS, GROWTH_BENEFITS, CUSTOM_BENEFITS } from "../PricingSection.data";

export function PricingTiers() {
  const prefersReducedMotion = useReducedMotion();
  const rocketIdle: TargetAndTransition | undefined = prefersReducedMotion
    ? undefined
    : { y: [0, -3, 0], transition: { duration: 3.2, repeat: Infinity, ease: "easeInOut" } };
  const rocketHover: TargetAndTransition | VariantLabels | undefined = prefersReducedMotion
    ? undefined
    : { y: -6, rotate: -12, scale: 1.08, transition: { type: "spring", stiffness: 300, damping: 14 } };
  const crownIdle: TargetAndTransition | undefined = prefersReducedMotion
    ? undefined
    : { y: [0, -2, 0], transition: { duration: 4, repeat: Infinity, ease: "easeInOut" } };
  const crownHover: TargetAndTransition | VariantLabels | undefined = prefersReducedMotion
    ? undefined
    : { scale: 1.1, y: -3, transition: { type: "spring", stiffness: 300, damping: 14 } };
  const sparkIdle: TargetAndTransition | undefined = prefersReducedMotion
    ? undefined
    : {
        rotate: [0, 360],
        scale: [1, 1.06, 1],
        transition: {
          rotate: { duration: 14, repeat: Infinity, ease: "linear" },
          scale: { duration: 3, repeat: Infinity, ease: "easeInOut" },
        },
      };
  const sparkHover: TargetAndTransition | VariantLabels | undefined = prefersReducedMotion
    ? undefined
    : { scale: 1.15, transition: { type: "spring", stiffness: 300, damping: 12 } };

  return (
    <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3 lg:items-stretch [&>*:nth-child(1)]:order-2 [&>*:nth-child(2)]:order-1 [&>*:nth-child(3)]:order-3">
      {/* Launch Plan — Most popular */}
      <div className="relative h-full lg:scale-[1.07] lg:z-10 lg:drop-shadow-[0_25px_50px_rgba(59,130,246,0.35)]">
        <div className="absolute -top-4 left-1/2 z-20 -translate-x-1/2">
          <span className="rounded-full bg-gradient-to-r from-brand-blue to-[#ec4899] px-5 py-1.5 text-xs font-semibold uppercase tracking-wider text-white shadow-lg">
            Most popular
          </span>
        </div>
        
        <ShineBorder borderRadius={16} borderWidth={4} duration={6} color={["#3b82f6", "#a855f7", "#ec4899"]} className="h-full relative">
          <GlowCard glowColor="blue" disableInnerGlow customSize className="w-full h-full p-8 sm:p-10 flex flex-col text-center items-center bg-card">

            <motion.div
              className="flex items-center justify-center hover:[filter:drop-shadow(0_6px_18px_rgba(59,130,246,0.55))] transition-[filter] duration-300"
              animate={rocketIdle}
              whileHover={rocketHover}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <defs>
                  <linearGradient id="rocketGradient" x1="0" y1="0" x2="24" y2="0" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#3b82f6" />
                    <stop offset="100%" stopColor="#ec4899" />
                  </linearGradient>
                </defs>
                <path
                  stroke="url(#rocketGradient)"
                  d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"
                />
                <path
                  stroke="url(#rocketGradient)"
                  d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"
                />
                <path
                  stroke="url(#rocketGradient)"
                  d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"
                />
                <path
                  stroke="url(#rocketGradient)"
                  d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"
                />
              </svg>
            </motion.div>
            <h3 className="mt-6 text-2xl font-bold text-foreground">Ecommerce</h3>
            <div className="my-5 h-px w-full bg-foreground/15" />
            <p className="text-sm text-muted-foreground font-medium text-gray-950">Starting at</p>
            <p className="text-5xl sm:text-6xl font-bold leading-none mt-2 bg-gradient-to-r from-brand-blue to-[#ec4899] bg-clip-text text-transparent">$3,495</p>
            <p className="mt-3 text-sm text-muted-foreground">Website, hosting, edits, and support</p>
            <div className="my-5 h-px w-full bg-foreground/15" />
            <p className="text-3xl sm:text-4xl font-bold leading-none bg-gradient-to-r from-brand-blue to-[#ec4899] bg-clip-text text-transparent">
              + $99<span className="text-lg sm:text-xl text-muted-foreground font-medium text-gray-950">/mo</span>
            </p>
            <p className="mt-3 text-sm text-muted-foreground">Hosting, updates, and support</p>
            <div className="my-5 h-px w-full bg-foreground/15" />
            <ul className="space-y-3 text-sm text-foreground text-left self-stretch">
              {LAUNCH_BENEFITS.map((b) => (
                <li key={b} className="flex items-center gap-3">
                  <Check className="h-5 w-5 text-brand-blue shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="my-5 h-px w-full bg-foreground/15" />
            <p className="text-xs text-muted-foreground leading-relaxed">
              Cancel anytime. You own the site outright. Hosting transfers to you if you leave.
            </p>
            <Button
              asChild
              className="cta-keep-gradient mt-6 w-full h-12 rounded-full font-semibold text-white bg-gradient-to-r from-brand-blue to-[#ec4899] hover:opacity-90"
            >
              <Link to="/get-mockup" search={{ plan: "launch" }} onClick={() => beginCheckout("launch")}>Get a Free Mockup</Link>
            </Button>
            <a href="#contact" className="mt-3 text-sm text-muted-foreground hover:text-brand-blue underline underline-offset-2 transition-colors">
              Book a Meeting
            </a>
          </GlowCard>
        </ShineBorder>
      </div>

      {/* Growth Monthly */}
      <GlowCard glowColor="blue" disableInnerGlow customSize className="w-full h-full p-8 sm:p-10 flex flex-col text-center items-center">
        <motion.div
          className="flex items-center justify-center text-brand-blue hover:[filter:drop-shadow(0_6px_18px_rgba(59,130,246,0.55))] transition-[filter] duration-300"
          animate={crownIdle}
          whileHover={crownHover}
        >
          <Crown className="h-12 w-12" />
        </motion.div>
        <h3 className="mt-6 text-2xl font-bold text-foreground">Business</h3>
        <div className="my-5 h-px w-full bg-foreground/15" />
        <p className="text-sm text-muted-foreground font-medium text-gray-950">Starting at</p>
        <p className="text-5xl sm:text-6xl font-bold leading-none text-brand-blue mt-2">
          $1,995
        </p>
        <p className="mt-3 text-sm text-muted-foreground">Website, hosting, edits, and support</p>
        <div className="my-5 h-px w-full bg-foreground/15" />
        <p className="text-3xl sm:text-4xl font-bold leading-none text-brand-blue">
          + $99<span className="text-lg sm:text-xl text-muted-foreground font-medium text-gray-950">&nbsp;/mo</span>
        </p>
        <p className="mt-3 text-sm text-muted-foreground">Hosting, updates, and support</p>
        <div className="my-5 h-px w-full bg-foreground/15" />
        <ul className="space-y-3 text-sm text-foreground text-left self-stretch">
          {GROWTH_BENEFITS.map((b) => (
            <li key={b} className="flex items-center gap-3">
              <Check className="h-5 w-5 text-brand-blue shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <div className="my-5 h-px w-full bg-foreground/15" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          Cancel anytime. You own the site outright. Hosting transfers to you if you leave.
        </p>
        <p className="mt-2 text-xs font-medium text-brand-blue leading-relaxed">
          Save 15% —  by paying annually upfront
        </p>
        <Button
          asChild
          variant="outline"
          className="mt-6 w-full h-12 rounded-full border-brand-blue text-brand-blue hover:bg-brand-blue/10 font-semibold"
        >
          <Link to="/get-mockup" search={{ plan: "growth" }} onClick={() => beginCheckout("growth")}>Get a Free Mockup</Link>
        </Button>
        <a href="#contact" className="mt-3 text-sm text-muted-foreground hover:text-brand-blue underline underline-offset-2 transition-colors">
          Book a Meeting
        </a>
      </GlowCard>

      {/* Custom Build */}
      <GlowCard glowColor="blue" disableInnerGlow customSize className="w-full h-full p-8 sm:p-10 flex flex-col text-center items-center">
        <motion.div
          className="flex items-center justify-center text-brand-blue hover:[filter:drop-shadow(0_6px_18px_rgba(168,85,247,0.55))] transition-[filter] duration-300"
          animate={sparkIdle}
          whileHover={sparkHover}
        >
          <Sparkles className="h-12 w-12" />
        </motion.div>
        <h3 className="mt-6 text-2xl font-bold text-foreground">Animated</h3>
        <div className="my-5 h-px w-full bg-foreground/15" />
        <p className="text-sm text-muted-foreground font-medium text-gray-950">Starting at</p>
        <p className="text-5xl sm:text-6xl font-bold text-brand-blue leading-none mt-2">$4,995</p>
        <p className="mt-3 text-sm text-muted-foreground">Scoped website build</p>
        <div className="my-5 h-px w-full bg-foreground/15" />
        <p className="text-3xl sm:text-4xl font-bold text-brand-blue leading-none">
          + $99<span className="text-lg sm:text-xl text-muted-foreground font-medium text-gray-950"> /mo</span>
        </p>
        <p className="mt-3 text-sm text-muted-foreground">Hosting, updates, and support</p>
        <div className="my-5 h-px w-full bg-foreground/15" />
        <ul className="space-y-3 text-sm text-foreground text-left self-stretch">
          {CUSTOM_BENEFITS.map((b) => (
            <li key={b} className="flex items-center gap-3">
              <Check className="h-5 w-5 text-brand-blue shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <div className="my-5 h-px w-full bg-foreground/15" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          Scope and quote confirmed on the call. You own the site outright. $99/mo hosting + support is cancel-anytime after launch.
        </p>
        <Button
          asChild
          variant="outline"
          className="mt-6 w-full h-12 rounded-full border-brand-blue text-brand-blue hover:bg-brand-blue/10 font-semibold"
        >
          <Link to="/get-mockup" search={{ plan: "custom" }} onClick={() => beginCheckout("custom")}>Get a Free Mockup</Link>
        </Button>
        <a href="#contact" className="mt-3 text-sm text-muted-foreground hover:text-brand-blue underline underline-offset-2 transition-colors">
          Book a Meeting
        </a>
      </GlowCard>
    </div>
  );
}
